import { __awaiter, __generator, __assign } from 'tslib';
import React__default, { useRef, useState, createRef, useEffect } from 'react';
import { fetcherModeAtom, userThumbnailTemplatesFetcherSelector, getUserThumbnailTemplatesSelector } from '@keepeek/commons';
import { Typography, Grow } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilRefresher_UNSTABLE } from 'recoil';
import KGrid from '../../Dataview/KGrid/index.js';
import { classes } from '../../Dataview/KGrid/index.styled.js';
import DefaultIcon from '../../DefaultIcon/index.js';
import KDialog from '../../KDialog/index.js';
import NoResult from '../../NoResult/index.js';
import { elementSelector } from '../../providers/elements/selectors.js';
import { thumbnailGeneratorPoiState } from '../../providers/poi/atoms.js';
import { currentUserSelector } from '../../providers/users/selectors.js';
import { isAdmin, isTechAdmin } from '../../providers/users/utils.js';
import SanitizeHtml from '../../SanitizeHtml/index.js';
import { withSuspense } from '../../withSuspense/index.js';
import Card from '../Card/index.js';
import { MAX_THUMBNAIL_TEMPLATE_TO_LOAD } from '../constants.js';
import { DataViewLoading } from '../DataViewLoading/index.js';
import ManageTemplate from '../ManageTemplate/index.js';
import { DEFAULT_ICON } from '../ManageTemplate/utils.js';
import { DATAVIEW_CSS_GRID_TEMPLATE_COLUMNS } from './utils.js';

var DataView = function DataView(_a) {
    var _b;
    var _this = this;
    var elementId = _a.elementId, enabledActions = _a.enabledActions, onInsert = _a.onInsert, _c = _a.testId, testId = _c === void 0 ? "KFThumbnailGeneratorDataView" : _c, _d = _a.KIcon, KIcon = _d === void 0 ? DefaultIcon : _d, _e = _a.displayCreateTemplate, displayCreateTemplate = _e === void 0 ? false : _e, onCloseDisplayCreateTemplate = _a.onCloseDisplayCreateTemplate, addExtensionAtEnd = _a.addExtensionAtEnd;
    // cards reference in order to be able to scroll to it after update / create card
    var cardsRef = useRef(new Map());
    var _f = useState(), scrollToTemplateId = _f[0], setScrollToTemplateId = _f[1];
    var enqueueSnackbar = useSnackbar().enqueueSnackbar;
    var t = useTranslation("refront-components").t;
    var _g = useState(), templateToDuplicate = _g[0], setTemplateToDuplicate = _g[1];
    var fetcherMode = useRecoilValue(fetcherModeAtom);
    var _h = useRecoilValue(userThumbnailTemplatesFetcherSelector(fetcherMode)), createUserThumbnailTemplate = _h.createUserThumbnailTemplate, updateUserThumbnailTemplate = _h.updateUserThumbnailTemplate, deleteUserThumbnailTemplate = _h.deleteUserThumbnailTemplate;
    var _j = useState(), updateTemplate = _j[0], setUpdateTemplate = _j[1];
    var _k = useState(), templateToDelete = _k[0], setTemplateToDelete = _k[1];
    var currentUser = useRecoilValue(currentUserSelector);
    var thumbnailTemplatesApiResult = useRecoilValue(getUserThumbnailTemplatesSelector({
        userId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
        size: MAX_THUMBNAIL_TEMPLATE_TO_LOAD
    }));
    var refreshTemplate = useRecoilRefresher_UNSTABLE(getUserThumbnailTemplatesSelector({
        userId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
        size: MAX_THUMBNAIL_TEMPLATE_TO_LOAD
    }));
    var element = useRecoilValue(elementSelector(elementId));
    var poi = useRecoilValue(thumbnailGeneratorPoiState({ elementId: elementId }));
    // once the template creation card is open, we scroll over it (necessary if we are at the bottom of the screen for example):
    var manageCreateTemplateRef = createRef();
    var _l = useState(false), createTemplateAnimationEnded = _l[0], setCreateTemplateAnimationEnded = _l[1];
    useEffect(function () {
        var _a;
        if ((displayCreateTemplate || templateToDuplicate) &&
            createTemplateAnimationEnded &&
            manageCreateTemplateRef.current) {
            (_a = manageCreateTemplateRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center"
            });
        }
    }, [
        createTemplateAnimationEnded,
        displayCreateTemplate,
        manageCreateTemplateRef,
        templateToDuplicate,
    ]);
    // Reset duplicate state if we are in creation mode:
    useEffect(function () {
        if (displayCreateTemplate) {
            setTemplateToDuplicate(undefined);
        }
    }, [displayCreateTemplate]);
    var thumbnailTemplates = thumbnailTemplatesApiResult &&
        thumbnailTemplatesApiResult._embedded &&
        thumbnailTemplatesApiResult._embedded["thumbnail-template"];
    // Scroll to specific card (usefull after update / create card)
    useEffect(function () {
        if (!scrollToTemplateId) {
            return;
        }
        var cardNode = cardsRef.current.get(scrollToTemplateId);
        if (cardNode) {
            cardNode.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center"
            });
            setScrollToTemplateId(undefined);
        }
    }, [scrollToTemplateId, thumbnailTemplates]);
    var resetManageTemplateState = function () {
        onCloseDisplayCreateTemplate && onCloseDisplayCreateTemplate();
        setTemplateToDuplicate(undefined);
        setUpdateTemplate(undefined);
        setCreateTemplateAnimationEnded(false);
    };
    var handleSubmitThumbnail = function (data, templateId) { return __awaiter(_this, void 0, void 0, function () {
        var apiData, response;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    apiData = {
                        userId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
                        label: data.name,
                        format: data.format,
                        width: parseInt(data.width),
                        height: parseInt(data.height),
                        icon: data.icon,
                        shared: data.shared
                    };
                    if (!templateId) return [3 /*break*/, 2];
                    // update
                    return [4 /*yield*/, updateUserThumbnailTemplate(__assign({ id: templateId }, apiData))];
                case 1:
                    // update
                    _a.sent();
                    enqueueSnackbar(t("components.kf-thumbnail-generator.dataview.template.update.success"), {
                        variant: "success"
                    });
                    setScrollToTemplateId(templateId);
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, createUserThumbnailTemplate(__assign({}, apiData))];
                case 3:
                    response = _a.sent();
                    enqueueSnackbar(t("components.kf-thumbnail-generator.dataview.template.create.success"), {
                        variant: "success"
                    });
                    setScrollToTemplateId(parseInt((response === null || response === void 0 ? void 0 : response.headers.location.substring((response === null || response === void 0 ? void 0 : response.headers.location.lastIndexOf("/")) + 1)) ||
                        ""));
                    _a.label = 4;
                case 4:
                    resetManageTemplateState();
                    refreshTemplate();
                    return [2 /*return*/];
            }
        });
    }); };
    var handleDeleteThumbnail = function () { return __awaiter(_this, void 0, void 0, function () {
        var clonedTemplateToDelete;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!templateToDelete) {
                        return [2 /*return*/];
                    }
                    clonedTemplateToDelete = __assign({}, templateToDelete);
                    setTemplateToDelete(undefined);
                    return [4 /*yield*/, deleteUserThumbnailTemplate({
                            userId: (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) || 0,
                            id: clonedTemplateToDelete.id || 0
                        })];
                case 1:
                    _a.sent();
                    enqueueSnackbar(t("components.kf-thumbnail-generator.dataview.template.delete.success"), {
                        variant: "success"
                    });
                    refreshTemplate();
                    return [2 /*return*/];
            }
        });
    }); };
    if ((thumbnailTemplatesApiResult === null || thumbnailTemplatesApiResult === void 0 ? void 0 : thumbnailTemplatesApiResult.totalCount) === 0 && !displayCreateTemplate) {
        return (React__default.createElement(NoResult, null,
            React__default.createElement(Typography, { variant: "h4", color: "text.secondary" },
                React__default.createElement(KIcon, { fontSize: "large", className: "fa-images", sx: {
                        verticalAlign: "middle",
                        marginRight: function (theme) { return theme.spacing(3); }
                    } }),
                t("components.kf-thumbnail-generator.dataview.noresult.title"))));
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(KGrid, { testId: testId, sx: (_b = {
                    width: "100%"
                },
                _b["& .".concat(classes.dataview)] = {
                    gridTemplateColumns: DATAVIEW_CSS_GRID_TEMPLATE_COLUMNS
                },
                _b), className: "KFThumbnailGeneratorDataView" },
            (displayCreateTemplate || templateToDuplicate) && (React__default.createElement(Grow, { "in": true, onEntered: function () { return setCreateTemplateAnimationEnded(true); } },
                React__default.createElement("li", { key: "".concat(displayCreateTemplate, "-").concat(JSON.stringify(templateToDuplicate)) },
                    React__default.createElement(ManageTemplate, { key: "".concat(displayCreateTemplate, "-").concat(JSON.stringify(templateToDuplicate)), updateMode: false, onSubmit: function (data) { return handleSubmitThumbnail(data); }, onCancel: resetManageTemplateState, isAdmin: isAdmin(currentUser) || isTechAdmin(currentUser), ref: manageCreateTemplateRef, defaultValues: templateToDuplicate && {
                            icon: templateToDuplicate.icon || DEFAULT_ICON,
                            height: "".concat(templateToDuplicate.height),
                            width: "".concat(templateToDuplicate.width),
                            name: templateToDuplicate.label,
                            format: templateToDuplicate.format,
                            shared: ((isAdmin(currentUser) || isTechAdmin(currentUser)) &&
                                templateToDuplicate.shared) ||
                                false
                        } })))),
            thumbnailTemplates &&
                thumbnailTemplates.map(function (thumbnailTemplate, i) {
                    var _a;
                    return thumbnailTemplate.id === updateTemplate ? (React__default.createElement(Grow, { "in": true },
                        React__default.createElement("li", { key: i },
                            React__default.createElement(ManageTemplate, { updateMode: true, onSubmit: function (data) { return handleSubmitThumbnail(data, thumbnailTemplate.id); }, onCancel: resetManageTemplateState, isAdmin: isAdmin(currentUser) || isTechAdmin(currentUser), defaultValues: {
                                    icon: thumbnailTemplate.icon || "",
                                    height: "".concat(thumbnailTemplate.height),
                                    width: "".concat(thumbnailTemplate.width),
                                    format: thumbnailTemplate.format,
                                    name: thumbnailTemplate.label,
                                    shared: (_a = thumbnailTemplate.shared) !== null && _a !== void 0 ? _a : false
                                } })))) : (React__default.createElement("li", { key: i, ref: function (node) {
                            if (thumbnailTemplate.id) {
                                if (node) {
                                    cardsRef.current.set(thumbnailTemplate.id, node);
                                }
                                else {
                                    cardsRef.current["delete"](thumbnailTemplate.id);
                                }
                            }
                        } },
                        React__default.createElement(Card, { element: element, poi: poi, thumbnailTemplate: thumbnailTemplate, index: i, enabledActions: enabledActions, onInsert: onInsert, KIcon: KIcon, onDelete: function () { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                                return [2 /*return*/, setTemplateToDelete(thumbnailTemplate)];
                            }); }); }, onUpdate: function () { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                                return [2 /*return*/, setUpdateTemplate(thumbnailTemplate.id || 0)];
                            }); }); }, onDuplicate: function () { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                                return [2 /*return*/, setTemplateToDuplicate(thumbnailTemplate)];
                            }); }); }, permission: thumbnailTemplate.permission, addExtensionAtEnd: addExtensionAtEnd })));
                })),
        React__default.createElement(KDialog, { open: !!templateToDelete, dialogContent: React__default.createElement(SanitizeHtml, { html: t("components.kf-thumbnail-generator.preview.action.delete.confirm", {
                    label: templateToDelete === null || templateToDelete === void 0 ? void 0 : templateToDelete.label
                }) }), titleLabel: t("components.kf-thumbnail-generator.preview.delete.action.title"), cancelLabel: t("components.kf-thumbnail-generator.preview.action.cancel"), actionLabel: t("components.kf-thumbnail-generator.preview.action.delete"), handleClose: function () { return setTemplateToDelete(undefined); }, handleAction: handleDeleteThumbnail })));
};
var DataView$1 = withSuspense(DataView, {
    LoadingComponent: DataViewLoading
});

export { DataView$1 as default };
//# sourceMappingURL=index.js.map
